import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Image, FileJson, X, RefreshCw, ExternalLink } from 'lucide-react';
import { supabase } from '../providers/supabase/supabase';
import type { BugReport, BugReportStatus } from './types';

const STATUS_CHIP: Record<
    BugReportStatus,
    { bgcolor: string; color: string; label: string }
> = {
    new: { bgcolor: '#fef2f2', color: '#dc2626', label: 'New' },
    working: {
        bgcolor: '#fffbeb',
        color: '#d97706',
        label: 'Working',
    },
    complete: {
        bgcolor: '#f0fdf4',
        color: '#16a34a',
        label: 'Complete',
    },
};

export default function AdminBugReportsTable() {
    const notify = useNotify();
    const [bugs, setBugs] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [detailBug, setDetailBug] = useState<BugReport | null>(null);
    const [descriptionBug, setDescriptionBug] = useState<BugReport | null>(
        null
    );
    const [screenshotBug, setScreenshotBug] = useState<BugReport | null>(null);
    const [statusFilter, setStatusFilter] = useState<BugReportStatus | 'all'>(
        'all'
    );
    const [sortField, setSortField] = useState<'created_at' | 'user_email'>(
        'created_at'
    );
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [resolveBug, setResolveBug] = useState<BugReport | null>(null);
    const [resolveNote, setResolveNote] = useState('');

    const fetchBugs = useCallback(async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('bug_reports')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching bugs:', error);
            notify('Failed to load bug reports', { type: 'error' });
        } else {
            setBugs((data ?? []) as BugReport[]);
        }
        setLoading(false);
    }, [notify]);

    useEffect(() => {
        fetchBugs();
    }, [fetchBugs]);

    const handleStatusChange = async (
        bug: BugReport,
        newStatus: BugReportStatus
    ) => {
        // Intercept "complete" to show resolution note dialog
        if (newStatus === 'complete') {
            setResolveBug(bug);
            setResolveNote('');
            return;
        }

        const { error } = await supabase
            .from('bug_reports')
            .update({ status: newStatus, updated_at: new Date().toISOString() })
            .eq('id', bug.id);

        if (error) {
            notify('Failed to update status', { type: 'error' });
        } else {
            setBugs(prev =>
                prev.map(b =>
                    b.id === bug.id ? { ...b, status: newStatus } : b
                )
            );
            notify('Status updated', { type: 'success' });
        }
    };

    const handleResolveConfirm = async () => {
        if (!resolveBug) return;

        const updates: Record<string, any> = {
            status: 'complete' as BugReportStatus,
            updated_at: new Date().toISOString(),
            resolution_note: resolveNote.trim() || null,
        };

        const { error } = await supabase
            .from('bug_reports')
            .update(updates)
            .eq('id', resolveBug.id);

        if (error) {
            notify('Failed to resolve bug', { type: 'error' });
        } else {
            setBugs(prev =>
                prev.map(b =>
                    b.id === resolveBug.id
                        ? {
                              ...b,
                              status: 'complete' as BugReportStatus,
                              resolution_note: updates.resolution_note,
                          }
                        : b
                )
            );
            notify('Bug resolved', { type: 'success' });
        }

        setResolveBug(null);
        setResolveNote('');
    };

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const filteredBugs = useMemo(() => {
        const filtered =
            statusFilter === 'all'
                ? bugs
                : bugs.filter(b => b.status === statusFilter);
        return [...filtered].sort((a, b) => {
            let diff: number;
            if (sortField === 'created_at') {
                diff =
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime();
            } else {
                diff = (a.user_email ?? '').localeCompare(b.user_email ?? '');
            }
            return sortDir === 'asc' ? diff : -diff;
        });
    }, [bugs, statusFilter, sortField, sortDir]);

    if (loading) {
        return (
            <Typography sx={{ color: '#64748b', py: 4 }}>
                Loading bug reports...
            </Typography>
        );
    }

    if (bugs.length === 0) {
        return (
            <Typography sx={{ color: '#94a3b8', py: 4 }}>
                No bug reports yet.
            </Typography>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Bug Reports ({filteredBugs.length})
                    </Typography>
                    {(['all', 'new', 'working', 'complete'] as const).map(
                        status => (
                            <Chip
                                key={status}
                                label={
                                    status === 'all'
                                        ? 'All'
                                        : STATUS_CHIP[status].label
                                }
                                size="small"
                                onClick={() => setStatusFilter(status)}
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    cursor: 'pointer',
                                    ...(statusFilter === status
                                        ? status === 'all'
                                            ? {
                                                  bgcolor: '#1e293b',
                                                  color: '#fff',
                                              }
                                            : {
                                                  bgcolor:
                                                      STATUS_CHIP[status]
                                                          .bgcolor,
                                                  color: STATUS_CHIP[status]
                                                      .color,
                                                  border: `1px solid ${STATUS_CHIP[status].color}`,
                                              }
                                        : {
                                              bgcolor: '#f1f5f9',
                                              color: '#64748b',
                                          }),
                                }}
                            />
                        )
                    )}
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchBugs} size="small">
                        <RefreshCw size={16} />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <TableSortLabel
                                    active={sortField === 'created_at'}
                                    direction={
                                        sortField === 'created_at'
                                            ? sortDir
                                            : 'asc'
                                    }
                                    onClick={() => {
                                        if (sortField === 'created_at') {
                                            setSortDir(prev =>
                                                prev === 'asc' ? 'desc' : 'asc'
                                            );
                                        } else {
                                            setSortField('created_at');
                                            setSortDir('desc');
                                        }
                                    }}
                                >
                                    Date
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                <TableSortLabel
                                    active={sortField === 'user_email'}
                                    direction={
                                        sortField === 'user_email'
                                            ? sortDir
                                            : 'asc'
                                    }
                                    onClick={() => {
                                        if (sortField === 'user_email') {
                                            setSortDir(prev =>
                                                prev === 'asc' ? 'desc' : 'asc'
                                            );
                                        } else {
                                            setSortField('user_email');
                                            setSortDir('asc');
                                        }
                                    }}
                                >
                                    User
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Description
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">
                                Screenshot
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">
                                Form Data
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredBugs.map(bug => {
                            const chip = STATUS_CHIP[bug.status];
                            return (
                                <TableRow
                                    key={bug.id}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: '#f8fafc',
                                        },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            fontSize: '0.8125rem',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {formatDate(bug.created_at)}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: '0.8125rem',
                                        }}
                                    >
                                        {bug.user_email}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: '0.8125rem',
                                            maxWidth: 300,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: '#3b82f6',
                                            },
                                        }}
                                        onClick={() => setDescriptionBug(bug)}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.8125rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                            }}
                                        >
                                            {bug.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {bug.screenshot_url ? (
                                            <Tooltip title="View screenshot">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setScreenshotBug(bug)
                                                    }
                                                >
                                                    <Image
                                                        size={16}
                                                        color="#3b82f6"
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: '#cbd5e1',
                                                }}
                                            >
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {bug.form_data &&
                                        Object.keys(bug.form_data).length >
                                            0 ? (
                                            <Tooltip title="View form data">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setDetailBug(bug)
                                                    }
                                                >
                                                    <FileJson
                                                        size={16}
                                                        color="#8b5cf6"
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: '#cbd5e1',
                                                }}
                                            >
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <FormControl
                                            size="small"
                                            sx={{ minWidth: 110 }}
                                        >
                                            <Select
                                                value={bug.status}
                                                onChange={e =>
                                                    handleStatusChange(
                                                        bug,
                                                        e.target
                                                            .value as BugReportStatus
                                                    )
                                                }
                                                sx={{
                                                    fontSize: '0.8125rem',
                                                    borderRadius: '6px',
                                                }}
                                                renderValue={val => (
                                                    <Chip
                                                        label={
                                                            STATUS_CHIP[
                                                                val as BugReportStatus
                                                            ].label
                                                        }
                                                        size="small"
                                                        sx={{
                                                            bgcolor:
                                                                STATUS_CHIP[
                                                                    val as BugReportStatus
                                                                ].bgcolor,
                                                            color: STATUS_CHIP[
                                                                val as BugReportStatus
                                                            ].color,
                                                            fontWeight: 500,
                                                            fontSize: '0.75rem',
                                                        }}
                                                    />
                                                )}
                                            >
                                                <MenuItem value="new">
                                                    New
                                                </MenuItem>
                                                <MenuItem value="working">
                                                    Working
                                                </MenuItem>
                                                <MenuItem value="complete">
                                                    Complete
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Screenshot Dialog */}
            <Dialog
                open={!!screenshotBug}
                onClose={() => setScreenshotBug(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 600, fontSize: '1rem' }}
                    >
                        Bug Screenshot
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {screenshotBug?.screenshot_url && (
                            <Tooltip title="Open in new tab">
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={screenshotBug.screenshot_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ExternalLink size={16} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton
                            size="small"
                            onClick={() => setScreenshotBug(null)}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    {screenshotBug?.screenshot_url && (
                        <Box
                            component="img"
                            src={screenshotBug.screenshot_url}
                            alt="Bug screenshot"
                            sx={{
                                width: '100%',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Description Dialog */}
            <Dialog
                open={!!descriptionBug}
                onClose={() => setDescriptionBug(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            component="span"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1rem',
                            }}
                        >
                            Bug Description
                        </Typography>
                        {descriptionBug && (
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                    mt: 0.25,
                                }}
                            >
                                {descriptionBug.user_email} &middot;{' '}
                                {formatDate(descriptionBug.created_at)}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setDescriptionBug(null)}
                    >
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    {descriptionBug && (
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                lineHeight: 1.6,
                                pt: 1,
                            }}
                        >
                            {descriptionBug.description}
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Resolution Note Dialog */}
            <Dialog
                open={!!resolveBug}
                onClose={() => setResolveBug(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 600, fontSize: '1rem' }}
                    >
                        Resolve Bug
                    </Typography>
                    {resolveBug && (
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                mt: 0.5,
                            }}
                        >
                            {resolveBug.user_email} &mdash;{' '}
                            {resolveBug.description.length > 100
                                ? `${resolveBug.description.slice(0, 100)}...`
                                : resolveBug.description}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        label="Resolution note (optional)"
                        placeholder="e.g. Fixed in latest update, or: This is expected behavior — here's how to..."
                        value={resolveNote}
                        onChange={e => setResolveNote(e.target.value)}
                        multiline
                        minRows={3}
                        maxRows={6}
                        fullWidth
                        sx={{ mt: 1 }}
                        helperText="This note will be shown to the user in their notification."
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setResolveBug(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleResolveConfirm}
                    >
                        Mark Complete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Form Data Dialog */}
            <Dialog
                open={!!detailBug}
                onClose={() => setDetailBug(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 600, fontSize: '1rem' }}
                    >
                        Captured Form Data
                    </Typography>
                    <IconButton size="small" onClick={() => setDetailBug(null)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2 }}>
                    {detailBug?.form_data && (
                        <Box
                            component="pre"
                            sx={{
                                fontSize: '0.8125rem',
                                bgcolor: '#f8fafc',
                                p: 2,
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                overflow: 'auto',
                                maxHeight: 400,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {JSON.stringify(detailBug.form_data, null, 2)}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
